import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, EmptyState, Loader } from "@/components/common";
import { TextField, AreaField } from "@/components/fields";
import { money, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { HandCoins } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Commissions() {
  const { user, company } = useAuth();
  const cur = company?.currency || "KES";
  const [items, setItems] = useState(null);
  const [pay, setPay] = useState(null);
  const [cheque, setCheque] = useState("");
  const [note, setNote] = useState("");
  const canPay = ["accountant", "property_manager"].includes(user.role);

  const load = () => api.get("/commissions").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try { const r = await api.post(`/commissions/${pay.id}/pay`, { cheque_number: cheque, note });
      toast.success(`Paid — voucher ${r.data.voucher_number}`); setPay(null); setCheque(""); setNote(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!items) return <Loader />;

  return (
    <div>
      <PageHeader title="Commissions" subtitle="Sourcing & letting staff commissions" icon={HandCoins} />
      {items.length === 0 ? (
        <EmptyState icon={HandCoins} title="No commissions yet" message="Commissions appear automatically when properties/tenants are sourced and fees are received." />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Staff</TableHead><TableHead>Type</TableHead><TableHead>Reference</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id} data-testid={`commission-row-${c.id}`}>
                    <TableCell className="font-medium">{c.staff_name || "—"}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{c.type}</Badge></TableCell>
                    <TableCell className="text-sm">{c.property_name || c.tenant_name || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{money(c.amount, cur)}</TableCell>
                    <TableCell><StatusBadge status={c.status} /></TableCell>
                    <TableCell className="text-right">
                      {canPay && c.status === "payable" && (
                        <Button size="sm" onClick={() => setPay(c)} data-testid={`pay-commission-${c.id}`}>Pay</Button>
                      )}
                      {c.status === "paid" && <span className="text-xs text-muted-foreground font-mono">{c.voucher_number}</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog open={!!pay} onOpenChange={() => setPay(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Pay Commission — {pay?.staff_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-sm">Amount: <span className="font-mono font-semibold">{money(pay?.amount, cur)}</span></div>
            <TextField label="Cheque Number" value={cheque} onChange={setCheque} testId="commission-cheque" />
            <AreaField label="Note" value={note} onChange={setNote} />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPay(null)}>Cancel</Button><Button onClick={submit} data-testid="confirm-pay-commission">Mark Paid & Voucher</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
